import { useEffect, useState, useCallback, useRef } from 'react';
import BrandBar from './BrandBar';
import Navbar from './Navbar';
import Hero from './Hero';
import Marquee from './Marquee';
import Showcase from './Showcase';
import HowItWorks from './HowItWorks';
import Features from './Features';
import Testimonials from './Testimonials';
import Pricing from './Pricing';
import FAQ from './FAQ';
import FinalCTA from './FinalCTA';
import Footer from './Footer';

// ── A/B Test: Assign dark or light on first visit ──
const getInitialTheme = () => {
  const stored = localStorage.getItem('gh_marketing_theme');
  if (stored === 'dark' || stored === 'light') return stored;

  // First visit: random 50/50 assignment
  const assigned = Math.random() < 0.5 ? 'dark' : 'light';
  localStorage.setItem('gh_marketing_theme', assigned);
  localStorage.setItem('gh_marketing_ab_variant', assigned); // Track original assignment

  return assigned;
};

const CursorLight = ({ theme }) => {
  const ref = useRef(null);
  const pos = useRef({ x: -200, y: -200 });
  const raf = useRef(null);

  const update = useCallback(() => {
    if (ref.current) {
      ref.current.style.transform = `translate(${pos.current.x - 300}px, ${pos.current.y - 300}px)`;
    }
    raf.current = requestAnimationFrame(update);
  }, []);

  useEffect(() => {
    const move = (e) => { pos.current = { x: e.clientX, y: e.clientY }; };
    const leave = () => { pos.current = { x: -600, y: -600 }; };
    window.addEventListener('mousemove', move, { passive: true });
    window.addEventListener('mouseleave', leave);
    raf.current = requestAnimationFrame(update);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseleave', leave);
      cancelAnimationFrame(raf.current);
    };
  }, [update]);

  return (
    <div
      ref={ref}
      data-testid="cursor-light"
      style={{
        position: 'fixed', width: '600px', height: '600px', borderRadius: '50%',
        background: theme === 'light'
          ? 'radial-gradient(circle, rgba(201,168,76,0.05) 0%, rgba(201,168,76,0.015) 30%, transparent 60%)'
          : 'radial-gradient(circle, rgba(201,168,76,0.07) 0%, rgba(201,168,76,0.02) 30%, transparent 60%)',
        pointerEvents: 'none', zIndex: 1, willChange: 'transform',
        transform: 'translate(-600px, -600px)',
      }}
    />
  );
};

const LandingPage = ({ onBuild }) => {
  const [scrollPct, setScrollPct] = useState(0);
  const [theme, setTheme] = useState(getInitialTheme);

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('gh_marketing_theme', next);
      return next;
    });
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('vis'); });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.rv').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    document.title = 'GoldenHour — AI Website Builder for Wedding Photographers & Videographers';
    const h = () => setScrollPct((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <div data-testid="app-root" className={theme === 'light' ? 'light-marketing' : ''} style={{ position: 'relative', background: 'var(--bg)', color: 'var(--fg)', transition: 'background .5s, color .5s' }}>
      <div className="grain" />
      <div className="light-leaks" />
      <CursorLight theme={theme} />
      <div className="scroll-progress" style={{ width: `${scrollPct}%`, background: 'var(--accent)' }} />
      <BrandBar theme={theme} />
      <Navbar onBuild={onBuild} theme={theme} onToggleTheme={toggleTheme} />
      <main>
        <Hero onBuild={onBuild} theme={theme} />
        <Marquee theme={theme} />
        <Showcase theme={theme} />
        <HowItWorks theme={theme} />
        <Features theme={theme} />
        <Testimonials theme={theme} />
        <Pricing onBuild={onBuild} theme={theme} />
        <FAQ theme={theme} />
        <FinalCTA onBuild={onBuild} theme={theme} />
      </main>
      <Footer theme={theme} />
      <ScrollTop />
    </div>
  );
};

const ScrollTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const h = () => setShow(window.scrollY > 600);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <button
      data-testid="scroll-to-top"
      className={`scroll-top ${show ? 'show' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      style={{ borderColor: show ? 'var(--accent)' : undefined }}
    >
      <span style={{ fontSize: '14px' }}>&#8593;</span>
    </button>
  );
};

export default LandingPage;
